import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, FormControl, Select, MenuItem } from "@mui/material";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Doughnut } from "react-chartjs-2";
import { postData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import Loader from "../../../common/spinner";

ChartJS.register(
    CategoryScale, LinearScale, PointElement,
    LineElement, ArcElement, Title, Tooltip, Legend
);

const monthToShort = {
    January: "jan", February: "feb", March: "mar", April: "apr",
    May: "may", June: "jun", July: "jul", August: "aug",
    September: "sep", October: "oct", November: "nov", December: "dec",
};

function HRPayrollDashboard() {
    const [dashboardMonth, setDashboardMonth] = useState("");
    const [dashboardMonthOptions, setDashboardMonthOptions] = useState([]);
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(false);
    const totalEmployeesColor = "#e3f2fd";
    const totalSalaryColor = "#f3e5f5";
    const pendingApprovalsColor = "#fff3e0";
    const lastPayrollColor = "#e8f5e8";

    // Generate Month Options
    useEffect(() => {
        const generateMonths = () => {
            const options = [];
            const today = new Date();
            for (let i = 0; i < 12; i++) {
                const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
                const monthYear = date.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                });
                options.push(monthYear);
            }
            return options;
        };

        const months = generateMonths();
        setDashboardMonthOptions(months);
        if (months.length > 0) setDashboardMonth(months[0]);
    }, []);

    // Fetch Dashboard Stats
    useEffect(() => {
        const fetchDashboard = async () => {
            if (!dashboardMonth) return;
            const [monthName, year] = dashboardMonth.split(" ");
            const payload = {
                month: monthToShort[monthName],
                year: Number(year),
            };

            setLoading(true);
            try {
                const res = await postData(API_ENDPOINTS.getPayrollReportDashboard, payload);
                if (res.success) {
                    setDashboardData(res.data || null);
                }
            } catch (err) {
                return null;
            } finally {
                setLoading(false);
            }
        };
        fetchDashboard();
    }, [dashboardMonth]);

    // Chart Data Configs
    const chartData = {
        labels: dashboardData?.monthlySalaryTrends?.map((i) => i.month) || [],
        datasets: [{
            label: "Monthly Salary Expenses",
            data: dashboardData?.monthlySalaryTrends?.map((i) => i.expenses) || [],
            borderColor: "#2196F3",
            backgroundColor: "rgba(33,150,243,0.1)",
            tension: 0.4,
        }],
    };

    const hasDeptData = dashboardData?.departmentWiseSalaryDistribution?.length > 0;
    const departmentSalaryData = {
        labels: hasDeptData
            ? dashboardData.departmentWiseSalaryDistribution.map((d) => d.department)
            : ["No Data Available"],
        datasets: [{
            data: hasDeptData ? dashboardData.departmentWiseSalaryDistribution.map((d) => d.totalSalary) : [1],
            backgroundColor: hasDeptData ? ["#2196F3", "#9C27B0", "#FF9800", "#4CAF50", "#F44336"] : ["#e0e0e0"],
            hoverOffset: hasDeptData ? 4 : 0,
        }],
    };

    if (loading) {
        return (
            <Box sx={{ height: "60vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Loader />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mr: 2 }}>Select Month-Year:</Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select value={dashboardMonth} onChange={(e) => setDashboardMonth(e.target.value)}>
                        {dashboardMonthOptions.map((month) => (
                            <MenuItem key={month} value={month}>{month}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Grid container spacing={3} alignItems="stretch">
                {/* Summary Cards */}
                {[
                    { label: "Total Employees", val: dashboardData?.summary?.totalEmployees, sub: dashboardData?.summary?.currentMonth, color: totalEmployeesColor },
                    { label: "Monthly Expenses", val: dashboardData?.summary?.totalMonthlyExpenses, color: totalSalaryColor },
                    { label: "Pending Approvals", val: dashboardData?.summary?.pendingTimesheetApprovals, color: pendingApprovalsColor },
                    { label: "Last Payroll Run", val: dashboardData?.summary?.lastPayrollRunDate?.slice(0, 10), color: lastPayrollColor }
                ].map((card, idx) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx} sx={{ display: "flex" }}>
                        <Box sx={{ p: 2, borderRadius: 2, boxShadow: 1, flex: 1, background: `linear-gradient(to right, ${card.color}, #ffffff)` }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">{card.label}</Typography>
                            <Typography variant={idx === 3 ? "body1" : "h5"} fontWeight="bold">{card.val ?? (idx === 3 ? "-" : 0)}</Typography>
                            {card.sub && <Typography variant="caption" color="textSecondary">{card.sub}</Typography>}
                        </Box>
                    </Grid>
                ))}

                {/* Charts */}
                <Grid size={{ xs: 12, sm: 6 }} sx={{ mt: 3 }}>
                    <Typography variant="h6" textAlign="center" gutterBottom>Monthly Salary Trends</Typography>
                    <Line data={chartData} options={{ responsive: true }} />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }} sx={{ mt: 3 }}>
                    <Typography variant="h6" textAlign="center" gutterBottom>Department-wise Salary Distribution</Typography>
                    <Box sx={{ maxHeight: 300, display: 'flex', justifyContent: 'center', position: 'relative' }}>
                        <Doughnut data={departmentSalaryData} options={{ cutout: "60%" }} />
                        {!hasDeptData && (
                            <Typography variant="caption" sx={{ position: 'absolute', top: '55%', color: 'text.secondary' }}>No Data Found</Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default HRPayrollDashboard;